/* eslint max-lines: ["error", { "max": 340, "skipBlankLines": true, "skipComments": true }] -- Keep the form lifecycle and its cancellation/SSO state together. */
/* eslint-disable @silverhand/fp/no-mutation -- Refs own the in-flight request and ephemeral proof without persisting credentials. */
import { InteractionEvent, SignInIdentifier } from '@logto/schemas';
import { HTTPError, TimeoutError } from 'ky';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import CaptchaContext from '@/Providers/CaptchaContextProvider/CaptchaContext';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import {
  sendContextualPhoneCode,
  submitContextualPhoneCode,
  type PhoneSignInConsent,
} from '@/apis/experience/contextual-sign-in';
import type { AuthenticationOptionsV3 } from '@/types/contextual-auth-options.generated';
import {
  canSendContextualSms,
  resolveContextualLoginIdentifier,
} from '@/utils/contextual-login-identifier';

import useErrorHandler from './use-error-handler';
import useGlobalRedirectTo from './use-global-redirect-to';
import usePasswordSignIn from './use-password-sign-in';
import useSingleSignOnWatch from './use-single-sign-on-watch';
import useSubmitInteractionErrorHandler from './use-submit-interaction-error-handler';
import useTerms from './use-terms';

export type ContextualSignInSettings = {
  options: AuthenticationOptionsV3;
  consent: Omit<PhoneSignInConsent, 'accepted'>;
  continuation?: 'wechat-account-proof';
};

const checkAborted = (signal: AbortSignal) => {
  if (signal.aborted) {
    throw new DOMException('Authentication cancelled', 'AbortError');
  }
};

/** This state lives only in the identity page. No OTP/password is put in Context storage. */
export default function useContextualSignIn({
  options,
  consent,
  continuation,
}: ContextualSignInSettings) {
  const [mode, setMode] = useState<'phone' | 'password'>('phone');
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [retryAt, setRetryAt] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const proof = useRef<{ phone: string; verificationId: string }>();
  const request = useRef<AbortController>();
  const pending = useRef(false);
  const active = useRef(true);
  const { executeCaptcha } = useContext(CaptchaContext);
  const { clearVerificationContext, setIdentifierInputValue } = useContext(UserInteractionContext);
  const preserveInteraction = continuation === 'wechat-account-proof';
  const passwordSignIn = usePasswordSignIn({ preserveInteraction });
  const handleError = useErrorHandler();
  const submitErrors = useSubmitInteractionErrorHandler(InteractionEvent.SignIn, { replace: true });
  const redirectTo = useGlobalRedirectTo();
  const { termsOfUseUrl, privacyPolicyUrl } = useTerms();
  const resolved = resolveContextualLoginIdentifier(identifier, mode);
  const ssoInput = useMemo(() => {
    const value = resolveContextualLoginIdentifier(identifier, mode);
    return mode === 'password' && value.ok && value.type === SignInIdentifier.Email
      ? { type: value.type, value: value.provider }
      : undefined;
  }, [identifier, mode]);
  const { showSingleSignOnForm, navigateToSingleSignOn } = useSingleSignOnWatch(ssoInput);
  useEffect(() => {
    if (showSingleSignOnForm) {
      setPassword('');
    }
  }, [showSingleSignOnForm]);
  const smsSupported = canSendContextualSms(resolved, options.sms_regions);

  useEffect(() => {
    active.current = true;
    clearVerificationContext();
    return () => {
      active.current = false;
      request.current?.abort();
      proof.current = undefined;
    };
  }, [clearVerificationContext]);

  useEffect(() => {
    const update = () => {
      setSeconds(Math.max(0, Math.ceil((retryAt - Date.now()) / 1000)));
    };
    update();
    if (retryAt <= Date.now()) {
      return;
    }
    const timer = window.setInterval(() => {
      update();
      if (retryAt <= Date.now()) {
        window.clearInterval(timer);
      }
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [retryAt]);

  function clearProof() {
    proof.current = undefined;
    setCode('');
    setPassword('');
    setNotice('');
    setError('');
    clearVerificationContext();
    passwordSignIn.clearErrorMessage();
  }

  function changeIdentifier(value: string) {
    if (pending.current) {
      return;
    }
    clearProof();
    setAgreed(false);
    setIdentifier(value);
  }

  function changeMode() {
    if (pending.current) {
      return;
    }
    clearProof();
    setAgreed(false);
    // Only a phone entered by this user can carry into OTP mode. A Handle or
    // email never discovers a private phone number and is not treated as one.
    setIdentifier(
      resolved.ok && resolved.type === SignInIdentifier.Phone ? resolved.canonical : ''
    );
    setMode(mode === 'phone' ? 'password' : 'phone');
  }

  function changeAgreement(value: boolean) {
    if (!pending.current) {
      setAgreed(value);
      setError('');
    }
  }

  const consentAvailable =
    Boolean(termsOfUseUrl && privacyPolicyUrl) &&
    /^[\dA-Za-z][\w.-]{0,63}$/.test(consent.termsVersion) &&
    /^[\dA-Za-z][\w.-]{0,63}$/.test(consent.privacyVersion);

  async function report(error: unknown) {
    if (!active.current) {
      return;
    }
    if (error instanceof TimeoutError) {
      setError('请求超时，请稍后重试。');
      return;
    }
    if (error instanceof HTTPError) {
      if (error.response.status === 429) {
        const delay = Number(error.response.headers.get('Retry-After'));
        setRetryAt(
          Date.now() + Math.max(60, Number.isFinite(delay) ? Math.min(delay, 600) : 60) * 1000
        );
        setError('操作过于频繁，请稍后重试。');
        return;
      }
      await handleError(error, {
        ...submitErrors,
        'verification_code.code_mismatch': () => {
          setError('验证码不正确，请重新输入。');
        },
        'verification_code.expired': () => {
          proof.current = undefined;
          setCode('');
          setError('验证码已过期，请重新获取。');
        },
        'session.invalid_interaction_type': () => {
          setError('本次登录已失效，请关闭后重新开始。');
        },
        'session.not_found': () => {
          setError('本次登录已过期，请重新开始。');
        },
        'user.missing_profile': () => {
          setError('当前注册方式暂不可用，请稍后重试。');
        },
        'user.user_not_exist': () => {
          setError('暂时无法完成登录，请稍后重试。');
        },
        global: () => {
          setError('暂时无法完成验证，请稍后重试。');
        },
      });
    } else {
      // Unknown exceptions may carry a request body: never log or display them.
      setError('网络连接未完成，请检查后重试。');
    }
  }

  async function run(action: (signal: AbortSignal) => Promise<void>) {
    if (pending.current || !active.current) {
      return;
    }
    pending.current = true;
    setBusy(true);
    setError('');
    passwordSignIn.clearErrorMessage();
    const controller = new AbortController();
    request.current = controller;
    try {
      await action(controller.signal);
    } catch (error) {
      if (!controller.signal.aborted) {
        await report(error);
      }
    } finally {
      pending.current = false;
      // React cleanup can run while the request is awaited.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (active.current) {
        setBusy(false);
      }
    }
  }

  function validatePhone(): string | undefined {
    if (!resolved.ok || resolved.type !== SignInIdentifier.Phone) {
      setError('请输入有效手机号；国际号码以 + 开头。');
      return;
    }
    if (!options.phone_otp_enabled || !smsSupported) {
      setError('暂不支持向这个地区发送短信，已有账号可使用密码登录。');
      return;
    }
    if (!consentAvailable) {
      setError('协议暂时无法加载，请稍后重试。');
      return;
    }
    if (!agreed) {
      setError('请先勾选同意使用条款和隐私政策。');
      return;
    }
    return resolved.provider;
  }

  async function sendCode() {
    if (pending.current || mode !== 'phone' || retryAt > Date.now()) {
      return;
    }
    const phone = validatePhone();
    if (!phone) {
      return;
    }
    await run(async (signal) => {
      proof.current = undefined;
      setCode('');
      setNotice('');
      const initialization = preserveInteraction
        ? { preserveInteraction: true as const }
        : await executeCaptcha();
      checkAborted(signal);
      const result = await sendContextualPhoneCode(
        phone,
        { ...consent, accepted: true },
        initialization,
        signal
      );
      checkAborted(signal);
      proof.current = { phone, verificationId: result.verificationId };
      setRetryAt(Date.now() + 60_000);
      setNotice('验证码已发送，请查看手机短信。');
    });
  }

  async function submit() {
    if (pending.current) {
      return;
    }
    if (mode === 'password') {
      if (!options.password_enabled) {
        return;
      }
      if (!resolved.ok || (!password && !showSingleSignOnForm)) {
        setError('请输入有效的手机号、账号或邮箱，以及密码。');
        return;
      }
      if (preserveInteraction && showSingleSignOnForm) {
        setError('请使用手机号或其他可用登录方式验证当前账号。');
        return;
      }
      const { type } = resolved;
      const value = type === SignInIdentifier.Username ? resolved.canonical : resolved.provider;
      await run(async () => {
        setIdentifierInputValue({ type, value });
        if (showSingleSignOnForm) {
          await navigateToSingleSignOn();
          return;
        }
        // This existing hook keeps the original CAPTCHA, SSO, MFA and recovery paths.
        await passwordSignIn.onSubmit({ identifier: { type, value }, password });
      });
      return;
    }
    const phone = validatePhone();
    if (!phone) {
      return;
    }
    const { current } = proof;
    if (!current || current.phone !== phone) {
      setError('请先获取当前手机号的验证码。');
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setError('请输入 6 位短信验证码。');
      return;
    }
    await run(async (signal) => {
      const result = await submitContextualPhoneCode(phone, code, current.verificationId, signal);
      checkAborted(signal);
      setCode('');
      proof.current = undefined;
      await redirectTo(result.redirectTo);
    });
  }

  return {
    mode,
    showSingleSignOnForm,
    identifier,
    code,
    password,
    agreed,
    busy,
    seconds,
    notice,
    resolved,
    error: error || (mode === 'password' ? passwordSignIn.errorMessage : ''),
    changeIdentifier,
    changeMode,
    changeAgreement,
    setCode,
    setPassword,
    sendCode,
    submit,
  };
}

/* eslint-enable @silverhand/fp/no-mutation */
